import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Package, PoundSterling, ShoppingCart, RefreshCw, Plus, Rocket, ShoppingBag
} from "lucide-react";

export const MerchantOverview = ({
  user, needsOnboarding, products, orders, totalRevenue, activeSubscriptions,
  onAddProduct, onAddCategory, onCreateAgreement
}) => {
  return (
    <div className="space-y-6">
      {/* Onboarding Banner */}
      {needsOnboarding && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-lg">Complete Your Setup</h3>
              <p className="text-indigo-100 text-sm mt-1">
                {user?.merchant_status === "pending_documents"
                  ? "Upload your business documents to get verified."
                  : user?.merchant_status === "pending_approval"
                  ? "Your documents are under review."
                  : "Complete the onboarding steps to start selling."}
              </p>
            </div>
            <Link to="/merchant/go-live">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50">
                <Rocket className="h-4 w-4 mr-2" />
                Go Live
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={Package} color="indigo" value={products.length} label="Products" />
        <StatCard icon={PoundSterling} color="emerald" value={`£${totalRevenue.toFixed(2)}`} label="Revenue" />
        <StatCard icon={ShoppingCart} color="amber" value={orders.length} label="Orders" />
        <StatCard icon={RefreshCw} color="cyan" value={activeSubscriptions} label="Subscriptions" />
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Button onClick={onAddProduct} className="bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
          <Button onClick={onAddCategory} variant="outline" className="rounded-full" data-testid="add-category-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
          <Button onClick={onCreateAgreement} variant="outline" className="rounded-full" data-testid="add-agreement-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Agreement
          </Button>
        </div>
      </div>

      {/* Recent Orders */}
      {orders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">Recent Orders</h2>
          <div className="space-y-3">
            {orders.slice(0, 5).map((order) => (
              <div key={order.order_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                    <ShoppingBag className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{order.customer_email || order.customer_id || "Customer"}</p>
                    <p className="text-xs text-slate-500">{order.items?.length || 0} items</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900">£{order.total_amount?.toFixed(2)}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    order.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  }`}>{order.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, color, value, label }) => {
  const colors = {
    indigo: "bg-indigo-100 text-indigo-600",
    emerald: "bg-emerald-100 text-emerald-600",
    amber: "bg-amber-100 text-amber-600",
    cyan: "bg-cyan-100 text-cyan-600"
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]?.split(' ')[0]}`}>
          <Icon className={`h-6 w-6 ${colors[color]?.split(' ')[1]}`} />
        </div>
        <div>
          <p className="text-2xl font-heading font-bold text-slate-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
};
